import React, { useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { Field, FieldArray, reduxForm } from "redux-form";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

import { customInput } from "../../../../common/custom-input";
import { MultiEmailInput } from "../../../../common/chipInputRedux";
import EmailEditor from "../../../../common/EmailEditor";
import DocumentImageContainer from "../JobSwmsForm/DocumentImageContainer";
import colourStyles from "../FormSelectStyle";

import styles from "./SwmsSendClientModal.module.css";
import v2Styles from "../../../v2Styles.module.css";

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className={styles.bannerIcon}>
    <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7.2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8.2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0-64 0 32 32 0 1 0 64 0z" />
  </svg>
);

const SwmsSendClientForm = ({
  handleSubmit,
  onSubmitForm,
  onClose,
  managers,
  loading,
  isSubmitting,
  initialValues,
  pdfLinkMissing
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [showAttachments, setShowAttachments] = useState(false);

  useEffect(() => {
    const bodyHtml = initialValues && initialValues.body ? `${initialValues.body}` : "";
    if (!bodyHtml) {
      setEditorState(EditorState.createEmpty());
      return;
    }

    const blocksFromHtml = htmlToDraft(bodyHtml);
    if (!blocksFromHtml || !blocksFromHtml.contentBlocks) {
      setEditorState(EditorState.createEmpty());
      return;
    }

    const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks);
    setEditorState(EditorState.createWithContent(contentState));
  }, [initialValues]);

  const onEditorStateChange = (value) => {
    setEditorState(value);
  };

  const submitForm = (values) => {
    const body = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    onSubmitForm({ ...values, body });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className={styles.form} aria-busy={isSubmitting === true}>
      {pdfLinkMissing && (
        <div className={`${styles.infoBanner} ${styles.warningBanner}`}>
          <WarningIcon />
          <span>Active version PDF link is unavailable. Please generate PDF before sending.</span>
        </div>
      )}

      <div className={styles.fieldGroup}>
        <label className={styles.label}>From</label>
        <Field
          name="from"
          type="text"
          component={customInput}
          className={`${v2Styles.TextField} ${styles.input} ${styles.fromInput}`}
          disabled
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>To</label>
        <Field
          name="to"
          component={MultiEmailInput}
          otherProps={{
            styles: colourStyles,
            isDisabled: loading || isSubmitting
          }}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>CC</label>
        <Field
          name="cc"
          component={MultiEmailInput}
          otherProps={{
            styles: colourStyles,
            options: managers,
            isDisabled: loading || isSubmitting
          }}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>BCC</label>
        <Field
          name="bcc"
          component={MultiEmailInput}
          otherProps={{
            styles: colourStyles,
            options: managers,
            isDisabled: loading || isSubmitting
          }}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Subject</label>
        <Field
          name="subject"
          type="text"
          component={customInput}
          className={`${v2Styles.TextField} ${styles.input} ${styles.subjectInput}`}
          disabled={loading || isSubmitting}
        />
      </div>

      <div className={styles.attachmentHeader}>
        <button
          type="button"
          className={styles.attachmentToggle}
          disabled={loading || isSubmitting}
          onClick={() => setShowAttachments((prev) => !prev)}
        >
          {showAttachments ? "-" : "+"} Attachments
        </button>
      </div>

      {showAttachments && (
        <div className={styles.attachmentPanel}>
          <FieldArray
            name="attachment_docs"
            loading={loading || isSubmitting}
            component={DocumentImageContainer}
            s3UploadPath="job-swms/email-attachments"
            fieldName="attachment_docs"
            formName="swmsSendClientForm"
            maxSize={20971520}
            label="Email Attachments"
          />
        </div>
      )}

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Body</label>
        <div className={styles.editorWrap}>
          <Field
            name="body"
            type="text"
            component={EmailEditor}
            disabled={loading || isSubmitting}
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            otherProps={{
              editorStyle: {
                minHeight: "260px"
              }
            }}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading || isSubmitting}>
          Cancel
        </button>
        <button type="submit" className={styles.sendBtn} disabled={loading || isSubmitting || pdfLinkMissing}>
          {isSubmitting ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Send to Client"}
        </button>
      </div>
    </form>
  );
};

export default reduxForm({
  form: "swmsSendClientForm",
  enableReinitialize: true
})(SwmsSendClientForm);
